import { Flex, Typography, theme } from 'antd';
import { coresDaOferta } from '@titularis/ui';
import type { StudyPerformance, StudyStatus } from '@titularis/api-client';
import { contar, dataCurta } from '../texto';

/**
 * A linha de estado da tela de início.
 *
 * **Até 17/09/2026, a Home de quem pagou era a de quem não pagou menos os
 * avisos.** Comparando as duas lado a lado, a diferença era subtrativa: sumia a
 * faixa amarela do limite, sumia o botão de desbloquear, e nada entrava no
 * lugar. Quem tinha acabado de gastar quinhentos reais voltava no dia seguinte
 * para exatamente a mesma tela de antes da compra.
 *
 * Esta linha é o acréscimo. Ela diz **duas** coisas, e as duas são do aluno:
 *
 * - o estado da conta - Pro, com o prazo, sob o degradê dos produtos, que é a
 *   mesma assinatura do cabeçalho do módulo e do selo do perfil;
 * - o que ele já fez - quantas resolveu e quanto acertou.
 *
 * Quem ainda é gratuito vê só o progresso, e só depois da primeira questão: a
 * cota já é dita na faixa do topo e na tela do módulo, e uma terceira menção
 * viraria a quarta chamada de venda da mesma dobra.
 */
export function EstadoDaConta({
  status,
  desempenho,
}: {
  status?: StudyStatus;
  desempenho?: StudyPerformance;
}) {
  const { token } = theme.useToken();

  const ativos = (status?.acessos ?? []).filter((acesso) => acesso.ativo);
  const pro = ativos.length > 0;

  /*
   * O prazo que interessa é o **mais próximo**: é ele que decide quando a
   * pessoa perde alguma coisa. Acesso sem prazo fica de fora da conta, senão
   * sequestraria o mínimo.
   */
  const proximoFim = ativos
    .map((acesso) => acesso.expiresAt)
    .filter((prazo): prazo is string => prazo !== null)
    .sort()[0];

  const total = desempenho?.total ?? 0;
  const percentual = desempenho?.percentual ?? null;

  if (!pro && total === 0) return null;

  return (
    <Flex vertical align="center" gap={6}>
      {pro ? (
        <Flex align="center" gap={8}>
          <span
            aria-hidden
            style={{
              width: 26,
              height: 3,
              borderRadius: 2,
              background: `linear-gradient(90deg, ${coresDaOferta.join(', ')})`,
            }}
          />
          <Typography.Text
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              color: token.colorText,
            }}
          >
            Pro
          </Typography.Text>
          {proximoFim ? (
            <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              até {dataCurta(proximoFim)}
            </Typography.Text>
          ) : null}
        </Flex>
      ) : null}

      {total > 0 ? (
        <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          {contar(total, 'questão resolvida', 'questões resolvidas')}
          {percentual !== null ? ` · ${percentual}% de acerto` : ''}
        </Typography.Text>
      ) : null}
    </Flex>
  );
}
